/**
 * Connector catalog — the single source of truth for connected-service metadata.
 *
 * Every current connector service type has exactly ONE entry here. This is the
 * one place a new connector adds its product metadata (label/description/
 * capabilities/credentials) AND its readiness shape (classification/installKind/
 * toolPrefix). Both the shared connected-service exports (`CONNECTED_SERVICE_TYPES`,
 * `connectedServiceDefinitionMap`, `CONNECTED_SERVICE_CATEGORIES`) and the backend
 * `readiness.ts` matrix derive from this array.
 *
 * Frontend-safe: this file lives in `@yagni/shared` (which cannot import backend),
 * so the frontend imports the derived exports directly. The backend layers its
 * richer readiness rows on top via a small overrides map; see
 * `packages/backend/src/connectors/readiness.ts`.
 *
 * ORDER MATTERS. `CONNECTED_SERVICE_TYPES` is derived from `CONNECTOR_CATALOG` in
 * declaration order and the frontend renders/serializes against it, so keep the
 * entries in the exact current order. A regression test
 * (`packages/shared/test/connectors/catalog.test.ts`) asserts the derived union.
 */
/**
 * Stable category ordering for `CONNECTED_SERVICE_CATEGORIES` output. Categories
 * are emitted in this order, each populated from the catalog entries whose
 * `category` matches (in catalog declaration order). Adding a connector to an
 * existing category does not change this list; a genuinely new category gets
 * appended here.
 *
 * `serviceOrder` is an optional intra-category ordering override. It exists only
 * to preserve byte-for-byte the previous hand-authored output where the order of
 * services within a category did not follow catalog declaration order — today
 * the sole case is `tools` (`mcp` before `custom`, while the catalog declares
 * `custom` before `mcp`). When omitted, services follow catalog order.
 */
export const CATALOG_CATEGORY_ORDER = [
    // Developer workflows lead (eng-org wedge, Gmail de-emphasis 2026-08-11);
    // Google Workspace stays fully supported, just not the headliner.
    { key: "developer", label: "Developer Tools" },
    { key: "tasks", label: "Issues & Project Tracking" },
    { key: "chat", label: "Chat" },
    { key: "google", label: "Google Workspace" },
    { key: "scheduling", label: "Scheduling" },
    { key: "email", label: "Email" },
    { key: "docs", label: "Docs & Knowledge" },
    { key: "payments", label: "Payments" },
    { key: "crm", label: "CRM" },
    { key: "infrastructure", label: "Infrastructure" },
    // social appended in catalog order; the override exists for mcp-before-custom.
    { key: "tools", label: "MCP & Custom Tools", serviceOrder: ["mcp", "custom", "social"] },
    { key: "recruiting", label: "Recruiting & Sourcing" },
    { key: "meetings", label: "Meetings & Video" },
];
export const CONNECTOR_CATALOG = [
    {
        id: "github",
        label: "GitHub",
        description: "Pull request review, issue tracking, and repository workflows.",
        category: "developer",
        capabilities: ["listPRs", "reviewPR", "mergePR", "createIssue"],
        defaultCapabilities: ["listPRs", "reviewPR", "mergePR", "createIssue"],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "github_app",
        toolPrefix: "github_",
        availability: "live",
        dataDomain: "workspace",
        // Repos are the container. Enumerated from the workspace_repos allowlist and
        // seeded access_override='workspace' (the allowlist is an explicit admin
        // choice), so behavior is unchanged; visibility.ts classifies future repos.
        containers: { kinds: [{ kind: "github_repo", label: "Repository" }] },
        metrics: [
            {
                // Merged pull requests in the trailing week — the first repository
                // computed Number (YAG-433) and the pilot's headline measure. A flow
                // count over the same deterministic signal the pr_merged receipt
                // reads: PRs project as `issue`-typed rows whose data.status is
                // "merged" exactly when pull_request.merged === true. Merged is
                // PR-only (issues close, they never merge), so no PR/issue
                // discriminator is needed. Windowed on updated_at_external — the
                // merge is the last projection-updating event for a PR in practice.
                key: "github.prs_merged_weekly",
                label: "PRs merged (7d)",
                unit: "",
                direction: "up",
                kind: "flow",
                source: "github",
                flow: {
                    objectType: "issue",
                    match: [{ field: "status", eq: "merged" }],
                    window: { field: "updated_at_external", days: 7 },
                    aggregate: { op: "count" },
                },
            },
        ],
        // A merged pull request is a deterministic receipt: the github `issue`-typed
        // external_object carries canonical `data.status === "merged"` (YAG-133).
        receipts: [{ check: "pr_merged", source: "github" }],
    },
    {
        id: "gitlab",
        label: "GitLab",
        description: "Projects, issues, merge requests, and CI pipelines. Read, plus draft issues and comments on approval.",
        category: "developer",
        capabilities: [
            "projects.list",
            "issues.list",
            "issues.get",
            "mergeRequests.list",
            "pipelines.list",
            "issues.create",
            "issues.comment",
        ],
        defaultCapabilities: [
            "projects.list",
            "issues.list",
            "issues.get",
            "mergeRequests.list",
            "pipelines.list",
        ],
        credentialFields: [
            {
                key: "apiKey",
                label: "Access Token",
                placeholder: "glpat-xxxxxxxxxxxx",
                inputType: "password",
                required: true,
                helperText: "A GitLab personal, project, or group access token with the api (or read_api) scope. Stored encrypted, never exposed to agents.",
            },
            {
                key: "baseUrl",
                label: "API Base URL",
                placeholder: "https://gitlab.com/api/v4",
                inputType: "text",
                required: false,
                helperText: "Only for self-managed GitLab. Leave blank for gitlab.com (defaults to https://gitlab.com/api/v4).",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "gitlab_",
        // Explicit early_access: no GitLab OAuth/app install path is provisioned
        // yet, so /services renders "Request early access" instead of a broken
        // Connect button. Flip to "live" in the change that provisions the install
        // path. (Every catalog entry must declare availability explicitly — the
        // planned.test.ts contract + the frontend Pick<…,"availability"> weak-type.)
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Developer tool surface with no external_objects projection today; a " +
                "future 'gitlab_project' container kind is plausible once issues/MRs " +
                "sync, but nothing is classified now.",
        },
    },
    {
        id: "figma",
        label: "Figma",
        description: "Designs linked in a ticket reach the Agent that builds it: rendered frames plus the layout, colors, type and text. Agents can also read a design and its comments on request.",
        category: "developer",
        capabilities: ["files.read", "comments.read"],
        defaultCapabilities: ["files.read", "comments.read"],
        credentialFields: [
            {
                key: "apiKey",
                label: "Access Token",
                placeholder: "figd_…",
                inputType: "password",
                required: true,
                helperText: "A Figma personal access token, or a plan access token on Organization and Enterprise plans, with the file_content:read and file_comments:read scopes. Create it from a Full or Dev seat: Figma allows View and Collab seats only a few file reads a month. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "figma_",
        // Live: the token install path is the generic encrypted credential form,
        // which works end to end with nothing to provision. An OAuth Connect needs
        // YAGNI's Figma app published and reviewed by Figma before people outside
        // YAGNI's own Figma organization can authorize it.
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Designs are read live from the links a ticket or a person names, with " +
                "the one workspace token; nothing is projected into external_objects, so " +
                "there is no container to classify.",
        },
    },
    {
        id: "bugsnag",
        label: "Bugsnag",
        description: "Bugsnag errors linked in a ticket reach the spec and the Agent that fixes them: the stack trace, the breadcrumbs, how often it happens and where. Agents can also read an error or event on request.",
        category: "developer",
        capabilities: ["errors.read", "events.read"],
        defaultCapabilities: ["errors.read", "events.read"],
        credentialFields: [
            {
                key: "apiKey",
                label: "Personal Auth Token",
                placeholder: "Bugsnag personal auth token",
                inputType: "password",
                required: true,
                helperText: "From Bugsnag: Personal settings → My account → Personal auth tokens. Bugsnag tokens can't be scoped, so YAGNI only reads with it. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "bugsnag_",
        // Live: the token install path is the generic encrypted credential form
        // (the Figma precedent); Bugsnag has no third-party OAuth to provision.
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Errors and events are read live from the links a ticket or a person " +
                "names, with the one workspace token; nothing is projected into " +
                "external_objects, so there is no container to classify.",
        },
    },
    {
        id: "google_workspace",
        label: "Google Workspace",
        description: "Gmail, Calendar, Drive, Sheets, Docs, and Slides — one OAuth connection.",
        category: "google",
        capabilities: [
            "gmail.readInbox",
            "gmail.getMessage",
            "gmail.draft",
            "gmail.send",
            "gmail.listLabels",
            "gmail.archive",
            "gmail.markRead",
            "gmail.markUnread",
            "gmail.addLabel",
            "gmail.removeLabel",
            "calendar.listEvents",
            "calendar.getEvent",
            "calendar.searchEvents",
            "calendar.listCalendars",
            "calendar.createEvent",
            "calendar.updateEvent",
            "calendar.deleteEvent",
            "contacts.search",
            "contacts.list",
            "contacts.get",
            "drive.listFiles",
            "drive.searchFiles",
            "drive.getFile",
            "drive.uploadFile",
            "drive.createFile",
            "sheets.create",
            "sheets.update",
            "docs.create",
            "docs.update",
            "slides.create",
            "slides.update",
        ],
        defaultCapabilities: [
            "gmail.readInbox",
            "gmail.getMessage",
            "gmail.listLabels",
            "calendar.listEvents",
            "calendar.getEvent",
            "calendar.searchEvents",
            "calendar.listCalendars",
            "contacts.search",
            "contacts.list",
            "contacts.get",
            "drive.listFiles",
            "drive.searchFiles",
            "drive.getFile",
            "sheets.create",
            "sheets.update",
            "docs.create",
            "docs.update",
            "slides.create",
            "slides.update",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        // Umbrella connector — backing connectors register as gmail_/gcal_ etc., so
        // there is no single native prefix. Readiness rows are hand-declared per
        // sub-connector (gmail, gcal) rather than derived from this entry.
        toolPrefix: null,
        availability: "live",
        dataDomain: "personal",
        containers: {
            model: "none",
            rationale: "Gmail, Calendar, and Drive follow the per-user Google model — every " +
                "synced item is stamped visibility='personal' to the connecting user, " +
                "so visibility rides owner_user_id, not a shared container.",
        },
        deskSources: [
            { id: "gmail", label: "Gmail", personalOnly: true, capabilityPrefixes: ["gmail."] },
            { id: "gcal", label: "Calendar", personalOnly: true, capabilityPrefixes: ["calendar."] },
            { id: "google_drive", label: "Google Drive", personalOnly: true, capabilityPrefixes: ["drive."] },
        ],
        // Gmail proves a reply landed; Calendar proves a meeting exists (YAG-133).
        receipts: [
            { check: "reply_received", source: "gmail" },
            { check: "event_exists", source: "gcal" },
        ],
    },
    {
        id: "outlook",
        label: "Microsoft Outlook",
        description: "Connect a Microsoft 365 / Outlook mailbox so its inbox mail feeds your Teams. Read-only in v1.",
        category: "email",
        capabilities: ["mail.read", "mail.search"],
        defaultCapabilities: ["mail.read", "mail.search"],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "outlook_",
        // early_access: the connector is built and merged, but no Microsoft Entra
        // (Azure AD) OAuth app is provisioned yet, so Connections renders "Request
        // early access", never a broken Connect button. Flip to 'live' in the same
        // change that registers the Entra app and wires OUTLOOK_CLIENT_ID /
        // OUTLOOK_CLIENT_SECRET.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single connected Microsoft 365 mailbox feeds the workspace (the " +
                "HubSpot/Notion/Read AI disclosure model). Microsoft Graph inbox " +
                "messages carry no per-item sharing container to classify, so there is " +
                "no container axis — every synced message is workspace-visible.",
        },
        deskSources: [{ id: "outlook", label: "Microsoft Outlook" }],
    },
    {
        id: "stripe",
        label: "Stripe",
        description: "Revenue, MRR, churn, and customer account visibility.",
        category: "payments",
        capabilities: ["getMetrics", "listCustomers"],
        defaultCapabilities: ["getMetrics", "listCustomers"],
        // API-key connect stays as the fallback path (the Stripe App OAuth
        // install is primary once STRIPE_APP_* is provisioned in an env; the
        // install route degrades gracefully when it isn't).
        credentialFields: [
            {
                key: "apiKey",
                label: "Secret Key",
                placeholder: "sk_live_xxxxxxxxxxxx",
                inputType: "password",
                required: true,
                helperText: "Store a Stripe secret key with read access.",
            },
        ],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "stripe_",
        availability: "live",
        dataDomain: "workspace",
        // One container per connected Stripe account (acct_…). Connecting an
        // account is an explicit admin grant (GitHub workspace_repos precedent),
        // so containers enumerate with accessOverride 'workspace' and items stay
        // workspace-visible; the container row exists for future per-account
        // reclassification and for multi-account workspaces.
        containers: {
            kinds: [{ kind: "account", label: "Stripe account" }],
        },
        metrics: [
            {
                // MRR is a point-in-time level — read live from the Stripe API at
                // snapshot time (sum active-subscription recurring amounts), not summed
                // over the partial invoice cache.
                key: "stripe.mrr",
                label: "MRR",
                unit: "$",
                direction: "up",
                kind: "stock",
                source: "stripe",
            },
        ],
        // A paid invoice is a deterministic receipt off the stripe `issue` cache
        // (canonical status / raw amount_paid) (YAG-133).
        receipts: [{ check: "payment_recorded", source: "stripe" }],
    },
    {
        id: "shopify",
        label: "Shopify",
        description: "Store revenue, orders, and customers from your Shopify shop.",
        category: "payments",
        capabilities: ["orders.list", "customers.list", "shop.read"],
        defaultCapabilities: ["orders.list", "customers.list", "shop.read"],
        credentialFields: [
            {
                key: "shopDomain",
                label: "Shop Domain",
                placeholder: "your-store.myshopify.com",
                inputType: "text",
                required: true,
                helperText: "Your permanent *.myshopify.com domain (not a custom domain).",
            },
            {
                key: "accessToken",
                label: "Admin API Access Token",
                placeholder: "shpat_xxxxxxxxxxxx",
                inputType: "password",
                required: true,
                helperText: "Admin API access token from a custom app with read_orders and read_customers scopes. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "first_class_source",
        installKind: "api_key",
        toolPrefix: "shopify_",
        // 'early_access' (fail-safe): renders "Request early access", never a broken
        // Connect button. No vendor install path is provisioned yet; flip to
        // availability: "live" in the change that ships it. Set explicitly rather
        // than omitted so the const-union entry shares the weak `availability`
        // property `getConnectorAvailability` picks (an omitted key trips TS's
        // weak-type "no properties in common" check).
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single shop's Admin API token exposes the whole store; orders and " +
                "customers carry no per-container ownership to classify, so items stay " +
                "workspace-visible.",
        },
    },
    {
        id: "hubspot",
        label: "HubSpot",
        description: "Sync your HubSpot CRM contacts and companies into YAGNI.",
        category: "crm",
        capabilities: ["contacts.list", "companies.list", "deals.list"],
        defaultCapabilities: ["contacts.list", "companies.list", "deals.list"],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "hubspot_",
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Portal install is an org-level grant; ownership is per-item, not " +
                "container. Owner/team is captured as row metadata for a future " +
                "per-user approximation (YAG-259 non-goal).",
        },
        metrics: [
            {
                // Open-pipeline value over target — a point-in-time level read live from
                // the HubSpot deals API at snapshot time (open deals where
                // hs_is_closed = false), never aggregated over the partial deal cache.
                // NEEDS A TARGET: coverage = open-pipeline value / target, so the
                // snapshot lane skips (computes null) until the desk sets a target.
                key: "hubspot.pipeline_coverage",
                label: "Pipeline coverage",
                unit: "x",
                direction: "up",
                kind: "stock",
                source: "hubspot",
            },
        ],
    },
    {
        id: "salesforce",
        label: "Salesforce",
        description: "Sync your Salesforce accounts, contacts, and opportunities into YAGNI.",
        category: "crm",
        capabilities: ["accounts.list", "contacts.list", "opportunities.list"],
        defaultCapabilities: ["accounts.list", "contacts.list", "opportunities.list"],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "salesforce_",
        // Declared 'early_access' (fail-safe): no Salesforce Connected App / vendor
        // OAuth client is provisioned yet, so the connector merges and renders
        // "Request early access" rather than a broken Connect button. Flip to
        // 'live' in the same change that provisions the install path.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "The Connected App install is an org-level OAuth grant; ownership is " +
                "per-record (OwnerId), not a container hierarchy, and the REST API " +
                "carries no per-object sharing/ACL signal to classify — the HubSpot " +
                "container-less model. Owner is captured as row metadata for a future " +
                "per-user approximation.",
        },
    },
    {
        id: "notion",
        label: "Notion",
        description: "Read workspace docs, query databases, and draft approved page/comment updates.",
        category: "docs",
        capabilities: [
            "pages.search",
            "pages.get",
            "dataSources.query",
            "pages.create",
            "pages.update",
            "comments.create",
        ],
        defaultCapabilities: [
            "pages.search",
            "pages.get",
            "dataSources.query",
            "pages.create",
            "pages.update",
            "comments.create",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "notion_",
        availability: "live",
        dataDomain: "workspace",
        // Top-level page/space is the container: workspace-shared → public,
        // integration-shared (private page shared with the bot) → private. The
        // public Notion API carries no per-page sharing signal, so today there is a
        // single public notion_space until YAG-274 lands real classification; the
        // live-path gate makes that future classification self-enforcing.
        containers: { kinds: [{ kind: "notion_space", label: "Space" }] },
    },
    {
        id: "confluence",
        label: "Confluence",
        description: "Read wiki pages and spaces from Atlassian Confluence Cloud so the agent can cite and reason over your team knowledge base.",
        category: "docs",
        capabilities: ["pages.search", "pages.get", "spaces.list"],
        defaultCapabilities: ["pages.search", "pages.get", "spaces.list"],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "confluence_",
        availability: "live",
        dataDomain: "workspace",
        // Spaces ARE the visibility container. Confluence's own model separates the
        // handbook from the comp bands by space, so projecting every page as
        // workspace-visible would flatten exactly the boundary the customer relies
        // on. Classification is conservative: personal spaces are 'private',
        // everything else is 'unknown' (we do not request the granular space
        // permission scope), and both fail closed — pages stay container-scoped
        // until an admin opens the space from Connections.
        containers: { kinds: [{ kind: "confluence_space", label: "Space" }] },
    },
    {
        id: "airtable",
        label: "Airtable",
        description: "Read bases, tables, and records, and create or update records on approval.",
        category: "docs",
        capabilities: [
            "bases.list",
            "tables.list",
            "records.list",
            "records.get",
            "records.create",
            "records.update",
        ],
        defaultCapabilities: ["bases.list", "tables.list", "records.list", "records.get"],
        credentialFields: [
            {
                key: "apiKey",
                label: "Personal Access Token",
                placeholder: "pat...",
                inputType: "password",
                required: true,
                helperText: "Create a token at airtable.com/create/tokens with schema.bases:read and data.records:read scopes (add data.records:write to enable record writes). Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "airtable_",
        // Ships as early_access (Request early access tile): the PAT form is wired,
        // but the install path has not been validated end to end. Flip to "live" in
        // the change that does.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single workspace Personal Access Token reads every accessible base; " +
                "Airtable exposes no per-container ACL to sync, and this is a tool-only " +
                "connector with no external_objects projection, so there is no " +
                "visibility container to classify.",
        },
    },
    {
        id: "database",
        label: "Database",
        description: "Read-only SQL access and schema inspection for analytics agents.",
        category: "infrastructure",
        capabilities: ["query", "schema"],
        defaultCapabilities: ["query", "schema"],
        credentialFields: [
            {
                key: "connectionString",
                label: "Connection String",
                placeholder: "postgres://readonly:password@host:5432/app",
                inputType: "password",
                required: true,
                helperText: "Use a read-only Postgres user whenever possible.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "manual_config",
        toolPrefix: "yagni_db_",
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A read-only SQL connection is a single workspace-level credential and " +
                "projects no external_objects, so there is no container to classify.",
        },
    },
    {
        id: "custom",
        label: "Custom Service",
        description: "Connect any REST API with key-based authentication.",
        category: "tools",
        capabilities: ["request"],
        defaultCapabilities: ["request"],
        credentialFields: [
            {
                key: "serviceName",
                label: "Service Name",
                placeholder: "livekit",
                inputType: "text",
                required: true,
                helperText: "A short identifier for this service (lowercase, e.g. 'livekit', 'twilio').",
            },
            {
                key: "baseUrl",
                label: "Base URL",
                placeholder: "https://api.example.com",
                inputType: "text",
                required: true,
                helperText: "The base URL for API requests. Paths in tool calls are appended to this.",
            },
            {
                key: "authScheme",
                label: "Auth Scheme",
                placeholder: "bearer",
                inputType: "select",
                required: true,
                helperText: "How to authenticate requests.",
                options: ["bearer", "api-key-header", "basic", "query-param"],
            },
            {
                key: "authParam",
                label: "Auth Parameter Name",
                placeholder: "X-API-Key",
                inputType: "text",
                required: false,
                helperText: "Header or query param name for auth. Required for api-key-header and query-param schemes.",
            },
            {
                key: "apiKey",
                label: "API Key / Token",
                placeholder: "sk-xxxxxxxxxxxx",
                inputType: "password",
                required: true,
                helperText: "The secret key or token. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "yagni_http_",
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Custom REST calls are tool-only and sync no source objects into the " +
                "cache, so there is no container hierarchy.",
        },
    },
    {
        id: "mcp",
        label: "MCP Server",
        description: "Connect a Model Context Protocol server to expose its tools to agents.",
        category: "tools",
        capabilities: ["tools.discover", "tools.invoke"],
        defaultCapabilities: ["tools.discover"],
        credentialFields: [
            {
                key: "url",
                label: "Server URL",
                placeholder: "https://mcp.example.com/sse",
                inputType: "text",
                required: true,
                helperText: "The Streamable HTTP endpoint of the MCP server.",
            },
            {
                key: "authToken",
                label: "Auth Token",
                placeholder: "Bearer token (optional)",
                inputType: "password",
                required: false,
                helperText: "Optional bearer token sent with every request.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "manual_config",
        toolPrefix: null,
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "MCP servers are an action surface, not a synced source — no " +
                "external_objects, so there is no container to classify.",
        },
    },
    {
        id: "asana",
        label: "Asana",
        description: "Read tasks and projects, and draft new tasks from email and chat.",
        category: "tasks",
        capabilities: ["tasks.search", "tasks.get", "tasks.create"],
        defaultCapabilities: ["tasks.search", "tasks.get", "tasks.create"],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "asana_",
        // The connector module is built and merged, but no Asana OAuth app is
        // provisioned yet, so /services renders "Request early access" rather than a
        // broken Connect button. Flip to availability: 'live' in the change that
        // provisions ASANA_CLIENT_ID/SECRET.
        availability: "early_access",
        dataDomain: "workspace",
        // Asana is a single org OAuth grant scoped to one workspace; per-task
        // ownership (assignee) is captured as row metadata, not a container. Project-
        // level privacy classification (public vs private projects) is a deferred
        // enhancement — until it lands, items stay workspace-visible. Mirrors the
        // HubSpot/Stripe org-grant model (YAG-259 non-goal).
        containers: {
            model: "none",
            rationale: "A single org OAuth grant scoped to one Asana workspace exposes tasks " +
                "workspace-wide; ownership is per-item (assignee), not a container. " +
                "Project-level privacy classification is a deferred enhancement.",
        },
    },
    {
        id: "linear",
        label: "Linear",
        description: "Read issues, draft new ones from email and chat, and pivot Linear ↔ YAGNI tickets.",
        category: "tasks",
        capabilities: [
            "issues.search",
            "issues.get",
            "issues.create",
            "issues.update",
        ],
        defaultCapabilities: [
            "issues.search",
            "issues.get",
            "issues.create",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "linear_",
        availability: "live",
        dataDomain: "workspace",
        writeIdentity: "personal",
        // Teams are the container: public teams → workspace, private teams →
        // connecting-user-only until granted (the leak this whole pass closes).
        containers: { kinds: [{ kind: "linear_team", label: "Team" }] },
        metrics: [
            {
                // Issues completed in the trailing window — a flow, counted over the
                // signal-driven external_objects cache (cache partiality is fine for a
                // windowed count).
                key: "linear.weekly_throughput",
                label: "Weekly throughput",
                unit: "",
                direction: "up",
                kind: "flow",
                source: "linear",
                // The existing hardcoded query, expressed as data: count `issue` rows
                // the connector projected as `state_type = 'completed'`, windowed to the
                // trailing 7 days on the issue's last external change time.
                flow: {
                    objectType: "issue",
                    match: [{ field: "state_type", eq: "completed" }],
                    window: { field: "updated_at_external", days: 7 },
                    aggregate: { op: "count" },
                },
            },
        ],
    },
    {
        id: "trello",
        label: "Trello",
        description: "Read boards, lists, and cards, and draft approved card creates, moves, and comments.",
        category: "tasks",
        capabilities: [
            "boards.list",
            "lists.list",
            "cards.list",
            "cards.get",
            "cards.search",
            "cards.create",
            "cards.move",
            "cards.comment",
        ],
        defaultCapabilities: [
            "boards.list",
            "lists.list",
            "cards.list",
            "cards.get",
            "cards.search",
        ],
        credentialFields: [
            {
                key: "apiKey",
                label: "API Key",
                placeholder: "Trello API key",
                inputType: "password",
                required: true,
                helperText: "Your Trello API key from https://trello.com/power-ups/admin. Stored encrypted, never exposed to agents.",
            },
            {
                key: "token",
                label: "API Token",
                placeholder: "Trello API token",
                inputType: "password",
                required: true,
                helperText: "A Trello token authorizing read/write on your boards. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "first_class_source",
        installKind: "api_key",
        toolPrefix: "trello_",
        // 'early_access' (declared explicitly — every catalog entry must): no Trello
        // Power-Up / API-key install path is provisioned yet, so Connections renders
        // "Request early access", never a broken Connect form.
        availability: "early_access",
        dataDomain: "workspace",
        // Boards are the container: a Trello token grants the connecting account's
        // full board access, but boards carry their own permission level
        // (private / org / public). Public boards → workspace-visible; private and
        // org boards fail closed to connecting-user-only until an admin grants them.
        containers: { kinds: [{ kind: "trello_board", label: "Board" }] },
    },
    {
        id: "slack",
        label: "Slack",
        description: "Bring @yagni into Slack through DMs and mentions, read invited channels, and draft or post approved messages back to Slack. Slack signal feeds your Teams.",
        category: "chat",
        capabilities: [
            "messages.search",
            "messages.read",
            "channels.list",
        ],
        defaultCapabilities: [
            "messages.search",
            "messages.read",
            "channels.list",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "slack_",
        availability: "live",
        dataDomain: "workspace",
        // Channels are the container: public → workspace, private → container,
        // shared/ext-shared (Slack Connect) → container (class 'shared', fail-closed).
        // NOTE(ops): the class-change events (channel_shared / channel_unshared /
        // channel_rename) must be enabled in the Slack app's Event Subscriptions for
        // live reclassification; until then a class change rides the 24h refresh
        // sweep. Slack emits no public→private conversion event, so that transition
        // is only healed by that sweep. See CONNECTOR_FRAMEWORK.md.
        containers: { kinds: [{ kind: "slack_channel", label: "Channel" }] },
        // A reply in a thread can land on Slack as well as Gmail; the same
        // `reply_received` check watches both sources (YAG-133).
        receipts: [{ check: "reply_received", source: "slack" }],
    },
    {
        id: "discord",
        label: "Discord",
        description: "Read server channels and messages, and post approved messages back to a channel. Connects with a bot token.",
        category: "chat",
        capabilities: [
            "guilds.list",
            "channels.list",
            "messages.read",
            "messages.send",
        ],
        defaultCapabilities: ["guilds.list", "channels.list", "messages.read"],
        credentialFields: [
            {
                key: "apiKey",
                label: "Bot Token",
                placeholder: "Discord bot token",
                inputType: "password",
                required: true,
                helperText: "Bot token from the Discord Developer Portal (Bot → Reset Token). Invite the bot to your server with the Read Messages and Send Messages permissions. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "discord_",
        // Explicit 'early_access' (renders "Request early access", never a Connect
        // button). No app-level OAuth client is provisioned and the Connections
        // install form/flow is not wired end to end yet. Flip to 'live' in the
        // change that provisions the install path.
        availability: "early_access",
        dataDomain: "workspace",
        // The descriptor carries schemas: [] — v1 is tool-only and projects nothing
        // into external_objects, so per-item visibility does not ride a container
        // hierarchy yet.
        containers: {
            model: "none",
            rationale: "Tool-only chat connector over a workspace bot token; guilds/channels/" +
                "messages are read on demand and not projected as external_objects yet, " +
                "so there is no container. A future 'discord_channel' kind is plausible " +
                "once messages sync as a first-class chat source.",
        },
    },
    // ── Recruiting / sourcing connectors (Calibratr Sourcing Autopilot) ──
    {
        id: "pin",
        label: "Pin",
        description: "AI candidate sourcing autopilot — creates searches, surfaces ranked candidates, and runs outreach on approval. Connects over Pin's hosted MCP server.",
        category: "recruiting",
        capabilities: [
            "pin.listJobs",
            "pin.getCandidates",
            "pin.readMemory",
            "pin.createJob",
            "pin.modifySearch",
            "pin.recalibrate",
            "pin.updateMemory",
            "pin.acceptCandidate",
            "pin.rejectCandidate",
        ],
        defaultCapabilities: [
            "pin.listJobs",
            "pin.getCandidates",
            "pin.createJob",
            "pin.acceptCandidate",
            "pin.rejectCandidate",
            "pin.recalibrate",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "tool_only",
        installKind: "oauth",
        toolPrefix: "pin_",
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Sourcing over the hosted Pin MCP server; candidates/jobs are not " +
                "projected as external_objects yet, so there is no container. A future " +
                "'ats_job' kind is plausible once they sync.",
        },
    },
    {
        id: "juicebox",
        label: "Juicebox",
        description: "AI candidate sourcing (PeopleGPT). Natural-language search across a large profile index with calibration feedback. Sourcing only.",
        category: "recruiting",
        capabilities: [
            "juicebox.search",
            "juicebox.getCandidates",
            "juicebox.submitFeedback",
            "juicebox.recalibrate",
            "juicebox.revealContact",
        ],
        defaultCapabilities: [
            "juicebox.search",
            "juicebox.getCandidates",
            "juicebox.submitFeedback",
        ],
        credentialFields: [
            {
                key: "apiKey",
                label: "API Key",
                placeholder: "jbx_...",
                inputType: "password",
                required: true,
                helperText: "Juicebox partner API key (Enterprise). Sourcing only.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "juicebox_",
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "PeopleGPT search is tool-only and syncs no source objects, so there " +
                "is no container hierarchy.",
        },
    },
    {
        id: "ashby",
        label: "Ashby",
        description: "Applicant tracking system — push sourced candidates into jobs, advance stages, and read pipeline.",
        category: "recruiting",
        capabilities: [
            "ats.listJobs",
            "ats.getCandidates",
            "ats.pushCandidate",
            "ats.advance",
            "ats.reject",
            "ats.addNote",
        ],
        defaultCapabilities: ["ats.listJobs", "ats.getCandidates", "ats.pushCandidate"],
        credentialFields: [
            {
                key: "apiKey",
                label: "API Key",
                placeholder: "Ashby API key",
                inputType: "password",
                required: true,
                helperText: "Generated by an Ashby admin in Settings → Integrations. Permissions are per-key.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "ashby_",
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "ATS tool surface with no external_objects projection today; a future " +
                "'ats_job' container kind is plausible once candidates sync, but nothing " +
                "is classified now.",
        },
    },
    {
        id: "greenhouse",
        label: "Greenhouse",
        description: "Applicant tracking system — push prospects, advance/reject applications, and log activity.",
        category: "recruiting",
        capabilities: [
            "ats.listJobs",
            "ats.getCandidates",
            "ats.pushCandidate",
            "ats.advance",
            "ats.reject",
            "ats.addNote",
        ],
        defaultCapabilities: ["ats.listJobs", "ats.getCandidates", "ats.pushCandidate"],
        credentialFields: [
            {
                key: "apiKey",
                label: "Harvest API Key",
                placeholder: "Greenhouse Harvest API token",
                inputType: "password",
                required: true,
                helperText: "Harvest API key from Configure → Dev Center → API Credential Management.",
            },
        ],
        // Native Greenhouse is tool-only for now. Edition/Desk projection is deferred
        // until the native connector projects source-pure ATS objects.
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "greenhouse_",
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "ATS tool surface with no external_objects projection today; a future " +
                "'ats_job' container kind is plausible once candidates sync, but nothing " +
                "is classified now.",
        },
    },
    {
        id: "lever",
        label: "Lever",
        description: "Applicant tracking system — create opportunities, advance/archive candidates, and log notes.",
        category: "recruiting",
        capabilities: [
            "ats.listJobs",
            "ats.getCandidates",
            "ats.pushCandidate",
            "ats.advance",
            "ats.reject",
            "ats.addNote",
        ],
        defaultCapabilities: ["ats.listJobs", "ats.getCandidates", "ats.pushCandidate"],
        credentialFields: [
            {
                key: "apiKey",
                label: "API Key",
                placeholder: "Lever Data API key",
                inputType: "password",
                required: true,
                helperText: "Lever Data API key from Settings → Integrations and API.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "lever_",
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "ATS tool surface with no external_objects projection today; a future " +
                "'ats_job' container kind is plausible once candidates sync, but nothing " +
                "is classified now.",
        },
    },
    {
        id: "social",
        label: "Social Publishing",
        description: "Draft and schedule Buffer social posts for review.",
        category: "tools",
        capabilities: ["social.read", "social.publish"],
        defaultCapabilities: ["social.read"],
        credentialFields: [
            {
                key: "apiKey",
                label: "Buffer API Key",
                placeholder: "Buffer API key",
                inputType: "password",
                required: true,
                helperText: "Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "social_",
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Buffer social publishing is a draft/schedule action surface with no " +
                "synced source objects, so there is no container to classify.",
        },
    },
    {
        id: "read_ai",
        label: "Read AI",
        description: "Sync Read AI meeting reports — summaries, action items, and key questions — into your Library and Teams.",
        category: "docs",
        capabilities: ["meetings.list", "meetings.get", "meetings.transcript"],
        defaultCapabilities: ["meetings.list", "meetings.get", "meetings.transcript"],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "read_ai_",
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Read AI meeting reports feed the workspace from the connecting account " +
                "(the HubSpot/Notion disclosure model), and the REST API carries no " +
                "per-meeting sharing or space signal to classify, so there is no " +
                "container axis.",
        },
    },
    {
        id: "fathom",
        label: "Fathom",
        description: "Sync Fathom meeting recordings — summaries, action items, highlights, and transcripts — into your Library and Teams.",
        category: "docs",
        capabilities: [
            "meetings.list",
            "meetings.get",
            "meetings.summary",
            "meetings.transcript",
        ],
        defaultCapabilities: [
            "meetings.list",
            "meetings.get",
            "meetings.summary",
            "meetings.transcript",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        // live requires the Fathom OAuth app provisioned in each environment:
        // FATHOM_CLIENT_ID / FATHOM_CLIENT_SECRET / FATHOM_API_BASE_URL, with the
        // callback URL registered on the Fathom app. Until then the connect flow
        // reports "Fathom OAuth is not configured" via readiness.
        availability: "live",
        installKind: "oauth",
        toolPrefix: "fathom_",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Fathom meeting recordings feed the workspace from the connecting " +
                "account (the HubSpot/Notion disclosure model), and the REST API " +
                "carries no per-meeting sharing container to classify, so there is no " +
                "container axis.",
        },
    },
    {
        id: "instantly",
        label: "Instantly",
        description: "Cold-outbound reply triage — sync campaigns, leads, and unibox replies; update lead status and pause leads on approval.",
        category: "crm",
        capabilities: [
            "campaigns.read",
            "leads.read",
            "emails.read",
            "leads.update",
        ],
        defaultCapabilities: ["campaigns.read", "leads.read", "emails.read"],
        credentialFields: [
            {
                key: "apiKey",
                label: "API Key",
                placeholder: "Instantly v2 API key",
                inputType: "password",
                required: true,
                helperText: "Instantly API v2 key (Settings → Integrations → API). Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "first_class_source",
        installKind: "api_key",
        toolPrefix: "instantly_",
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single workspace API key ingests all campaigns; Instantly exposes no " +
                "per-container ACL — ownership is per-campaign metadata, not a " +
                "visibility container.",
        },
        deskSources: [{ id: "instantly", label: "Instantly" }],
        // A lead whose interest status was updated is a deterministic receipt
        // (`lead_status_updated`): the Instantly `lead` is projected to a canonical
        // `contact` external_object whose `data.fields.interest_status` carries the
        // stable label. Pairs with the `RECEIPT_CHECKS` registry entry + the
        // VerifyCheckKind (YAG-239); the receipt-capability conformance gate asserts
        // catalog ↔ registry lockstep.
        receipts: [{ check: "lead_status_updated", source: "instantly" }],
    },
    {
        id: "intercom",
        label: "Intercom",
        description: "Sync Intercom conversations, contacts, and companies into your Teams and Library, and search them from @yagni.",
        category: "crm",
        capabilities: [
            "conversations.read",
            "contacts.read",
            "companies.read",
        ],
        defaultCapabilities: [
            "conversations.read",
            "contacts.read",
            "companies.read",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "intercom_",
        // availability EXPLICIT 'early_access' (fail-safe): no Intercom vendor OAuth
        // app is provisioned yet, so /services renders "Request early access", never a
        // broken Connect button. Flip to 'live' in the change that provisions the
        // OAuth app + wires the /api/auth/intercom install route.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single workspace OAuth install exposes the whole Intercom account " +
                "(conversations, contacts, companies); Intercom's REST API carries no " +
                "per-inbox/team ACL to classify, so ownership is per-item metadata, not " +
                "a visibility container (the HubSpot org-grant model).",
        },
        deskSources: [{ id: "intercom", label: "Intercom" }],
    },
    {
        id: "jira",
        label: "Jira",
        description: "Read issues, surface the ones that need a decision, and draft new issues for your team. Issue creation is approval gated.",
        category: "tasks",
        capabilities: [
            "issues.search",
            "issues.get",
            "issues.create",
        ],
        defaultCapabilities: [
            "issues.search",
            "issues.get",
            "issues.create",
        ],
        credentialFields: [],
        oauthFlow: true,
        readinessClassification: "first_class_source",
        installKind: "oauth",
        toolPrefix: "jira_",
        availability: "live",
        dataDomain: "workspace",
        writeIdentity: "personal",
        // Projects are visibility containers (fail-closed: isPrivate → 'private',
        // everything else 'unknown' — permission schemes are not sync-capturable,
        // so nothing classifies 'public'). Issues land visibility='container'
        // until an admin opens a project from Connections or grants it to a Team.
        containers: { kinds: [{ kind: "jira_project", label: "Project" }] },
        deskSources: [{ id: "jira", label: "Jira" }],
    },
    {
        id: "zendesk",
        label: "Zendesk",
        description: "Customer-support source — sync Zendesk tickets, users, and organizations into your Teams and Library, and read them from @yagni.",
        category: "crm",
        capabilities: [
            "tickets.read",
            "users.read",
            "organizations.read",
        ],
        defaultCapabilities: [
            "tickets.read",
            "users.read",
            "organizations.read",
        ],
        credentialFields: [
            {
                key: "subdomain",
                label: "Subdomain",
                placeholder: "acme (from acme.zendesk.com)",
                inputType: "text",
                required: true,
                helperText: "Your Zendesk instance subdomain — the acme in acme.zendesk.com.",
            },
            {
                key: "email",
                label: "Agent Email",
                placeholder: "agent@acme.com",
                inputType: "text",
                required: true,
                helperText: "The agent email the API token belongs to. Used for the API-token Basic auth scheme.",
            },
            {
                key: "apiToken",
                label: "API Token",
                placeholder: "Zendesk API token",
                inputType: "password",
                required: true,
                helperText: "Admin Center → Apps and integrations → Zendesk API → add an API token. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "first_class_source",
        installKind: "api_key",
        toolPrefix: "zendesk_",
        // 'early_access' (fail-safe): renders "Request early access", never a broken
        // Connect button, because no vendor OAuth app / install path is provisioned
        // yet. Set explicitly (rather than relying on the omitted-default) so the
        // literal entry satisfies getConnectorAvailability's weak parameter type.
        // Flip to "live" in the change that provisions the install path.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single workspace API token ingests every ticket. Zendesk's groups " +
                "and brands are ticket metadata, not a visibility container enforced " +
                "through the shared credential, so there is no container axis to " +
                "classify (the HubSpot/Instantly single-credential model). A future " +
                "'zendesk_group' or 'zendesk_brand' kind is plausible if per-group " +
                "access is ever modeled.",
        },
        deskSources: [{ id: "zendesk", label: "Zendesk" }],
    },
    // ── Meetings & Video ──
    {
        id: "zoom",
        label: "Zoom",
        description: "Read meetings, cloud recordings + transcripts, and past participants so @yagni can brief on what happened.",
        category: "meetings",
        capabilities: [
            "meetings.list",
            "meetings.get",
            "recordings.list",
            "recordings.get",
            "users.list",
            "participants.list",
        ],
        defaultCapabilities: [
            "meetings.list",
            "meetings.get",
            "recordings.list",
            "recordings.get",
            "users.list",
            "participants.list",
        ],
        credentialFields: [
            {
                key: "accountId",
                label: "Account ID",
                placeholder: "Zoom account ID",
                inputType: "text",
                required: true,
                helperText: "From your Zoom Server-to-Server OAuth app (Account ID). Used to mint account-level tokens.",
            },
            {
                key: "clientId",
                label: "Client ID",
                placeholder: "Zoom Server-to-Server OAuth client ID",
                inputType: "text",
                required: true,
                helperText: "The Server-to-Server OAuth app's Client ID.",
            },
            {
                key: "clientSecret",
                label: "Client Secret",
                placeholder: "Zoom Server-to-Server OAuth client secret",
                inputType: "password",
                required: true,
                helperText: "Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "zoom_",
        // 'early_access' renders "Request early access", never a broken Connect
        // button — no vendor Server-to-Server OAuth app is provisioned yet. This is
        // the omitted-field default made explicit (the availability contract, #1294,
        // requires every authored entry to declare it). Flip to "live" in the change
        // that provisions the install path.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Tool-first read connector with no external_objects projection yet, so " +
                "there is nothing to classify; the account-level Server-to-Server OAuth " +
                "credential also exposes no per-meeting sharing signal to key a container " +
                "off. Add a container axis when Zoom is promoted to a first-class source.",
        },
    },
    {
        id: "calendly",
        label: "Calendly",
        description: "Read scheduled meetings, event types, and invitees from Calendly so booked time flows into Briefs, Teams, and the Calendar agenda.",
        category: "scheduling",
        capabilities: [
            "events.list",
            "events.invitees",
            "eventTypes.list",
        ],
        defaultCapabilities: [
            "events.list",
            "events.invitees",
            "eventTypes.list",
        ],
        credentialFields: [
            {
                key: "apiKey",
                label: "Personal Access Token",
                placeholder: "eyJ... (Calendly personal access token)",
                inputType: "password",
                required: true,
                helperText: "Create a personal access token at calendly.com → Integrations → API & webhooks. Use an admin/owner token to read organization-wide scheduled events. Stored encrypted, never exposed to agents.",
            },
        ],
        readinessClassification: "first_class_source",
        installKind: "api_key",
        toolPrefix: "calendly_",
        // early_access (declared explicitly — planned.test.ts requires every entry
        // to state its availability): the Calendly install path (personal-access-
        // token connection form) is not provisioned yet, so Connections renders
        // "Request early access", never a broken Connect button. Flip to 'live' in
        // the same change that provisions the install path.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single org/admin personal access token exposes the whole Calendly " +
                "organization's scheduled events; there is no per-container ownership " +
                "to classify, so booked meetings stay workspace-visible.",
        },
    },
    {
        id: "sentry",
        label: "Sentry",
        description: "Sync Sentry issues — grouped production errors — into your Teams and Briefs, with read tools to search and inspect them.",
        category: "developer",
        capabilities: ["issues.list", "issues.get"],
        defaultCapabilities: ["issues.list", "issues.get"],
        credentialFields: [
            {
                key: "apiKey",
                label: "Auth Token",
                placeholder: "sntrys_… or a personal auth token",
                inputType: "password",
                required: true,
                helperText: "A Sentry organization auth token (Settings → Auth Tokens) or a user auth token with org:read, project:read, and event:read. Stored encrypted, never exposed to agents.",
            },
            {
                key: "org",
                label: "Organization Slug",
                placeholder: "acme",
                inputType: "text",
                required: true,
                helperText: "The Sentry organization slug — the `acme` in sentry.io/organizations/acme.",
            },
            {
                key: "host",
                label: "API Host",
                placeholder: "https://sentry.io",
                inputType: "text",
                required: false,
                helperText: "Only for self-hosted Sentry. Leave blank for sentry.io.",
            },
        ],
        readinessClassification: "first_class_source",
        installKind: "api_key",
        toolPrefix: "sentry_",
        // Ships as early_access (Request early access tile) until the org-token
        // install path is validated end to end; flip to "live" in that change.
        availability: "early_access",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "A single organization auth token ingests every issue the token can " +
                "see; Sentry's issue payload carries project metadata but no per-item " +
                "ACL a workspace member would be scoped by, so there is no visibility " +
                "container to classify (the HubSpot/Instantly single-workspace-key model).",
        },
        metrics: [
            {
                // Grouped error issues seen in the trailing week — a flow count over
                // synced sentry issues (the first engineering Number, wave 1).
                // Windowed on updated_at_external (= last seen), so this counts
                // ACTIVE issues, not first-seen: the only FlowWindowField is
                // updated_at_external, and the stats-API-backed true error rate is a
                // named follow-up ticket, not this recipe.
                key: "sentry.weekly_active_issues",
                label: "Active error issues (7d)",
                unit: "",
                direction: "down",
                kind: "flow",
                source: "sentry",
                flow: {
                    objectType: "issue",
                    window: { field: "updated_at_external", days: 7 },
                    aggregate: { op: "count" },
                },
            },
        ],
    },
    {
        // The customer's own Datadog, read-only. Unrelated to YAGNI's own telemetry.
        id: "datadog",
        label: "Datadog",
        description: "Logs, Error Tracking issues, monitors and traces behind a ticket reach the spec and the plan. Agents can also search logs and read an issue or a monitor on request. Read-only.",
        category: "developer",
        capabilities: ["logs.read", "error_tracking.read", "monitors.read", "apm.read"],
        defaultCapabilities: ["logs.read", "error_tracking.read", "monitors.read", "apm.read"],
        credentialFields: [
            {
                key: "apiKey",
                label: "API Key",
                placeholder: "32-character API key",
                inputType: "password",
                required: true,
                helperText: "Organization Settings → API Keys. Stored encrypted, never exposed to agents.",
            },
            {
                key: "appKey",
                label: "Application Key",
                placeholder: "40-character application key",
                inputType: "password",
                required: true,
                helperText: "Organization Settings → Application Keys. Create a read-scoped key with only logs_read_data, error_tracking_read, monitors_read and apm_read. YAGNI never writes to Datadog. Stored encrypted, never exposed to agents.",
            },
            {
                key: "site",
                label: "Site",
                placeholder: "datadoghq.com",
                inputType: "select",
                required: true,
                helperText: "Where your Datadog lives: datadoghq.com is US1, us3/us5.datadoghq.com are US3/US5, datadoghq.eu is EU1, ap1/ap2.datadoghq.com are AP1/AP2, uk1.datadoghq.com is UK1, ddog-gov.com is US1-FED. It is the host you sign in on, without app.",
                options: [
                    "datadoghq.com",
                    "us3.datadoghq.com",
                    "us5.datadoghq.com",
                    "datadoghq.eu",
                    "ap1.datadoghq.com",
                    "ap2.datadoghq.com",
                    "uk1.datadoghq.com",
                    "ddog-gov.com",
                    "us2.ddog-gov.com",
                ],
            },
        ],
        readinessClassification: "tool_only",
        installKind: "api_key",
        toolPrefix: "datadog_",
        // Live: the key install path is the generic encrypted credential form,
        // which works end to end with nothing to provision.
        availability: "live",
        dataDomain: "workspace",
        containers: {
            model: "none",
            rationale: "Logs, issues, monitors and traces are read live from the links a ticket " +
                "names or a tool call asks for, with the one workspace key pair; nothing " +
                "is projected into external_objects, so there is no container to classify.",
        },
    },
];
const connectorCatalogEntryMap = new Map(CONNECTOR_CATALOG.map((entry) => [entry.id, entry]));
/** Catalog entry lookup by service type id; undefined for unknown ids. */
export function getConnectorCatalogEntry(id) {
    return connectorCatalogEntryMap.get(id);
}
export function getConnectorAvailability(entry) {
    return entry.availability ?? "early_access";
}
export function connectorWriteIdentity(serviceType) {
    const entry = CONNECTOR_CATALOG.find((e) => e.id === serviceType);
    return entry?.writeIdentity ?? "workspace";
}
//# sourceMappingURL=catalog.js.map